/**
 * The visualisations a report card can be switched between.
 *
 * Every card carries this control, because which shape answers a question best
 * depends on the question being asked that morning: the same SLA figures are a
 * trend when you are watching a change land and a table when you are pasting
 * numbers into a board pack.
 *
 * What a card does *not* do is offer all six regardless. A donut of a twelve-
 * month trend and a line across unordered categories are both drawable and
 * neither means anything, and a control that produces nonsense on two of its
 * settings teaches people not to trust the other four. So each card declares
 * the set its data can honestly express — every card offering the table, which
 * always can — and the dropdown shows that set.
 */

export const CHART_TYPES = [
  "bar",
  "line",
  "area",
  "stacked-bar",
  "donut",
  "table",
  "line-split",
] as const;

export type ChartType = (typeof CHART_TYPES)[number];

export const CHART_LABELS: Record<ChartType, string> = {
  bar: "Bar",
  line: "Line",
  area: "Area",
  "stacked-bar": "Stacked bar",
  donut: "Donut",
  table: "Table",
  "line-split": "Line split",
};

/**
 * `line-split` is in neither preset below, and that is how it stays private.
 *
 * A card's dropdown is built from the set it passes to `ReportCard`, and every
 * card but one passes `CATEGORY_CHARTS` or `TIME_SERIES_CHARTS`. Leaving this
 * out of both means the closed report can name it directly and nothing else
 * can reach it -- no other dropdown gains an option.
 *
 * It is a member of the union rather than a string of its own because it is
 * still a chart type: `resolveChart` has to be able to fall back from it when
 * a stored layout names it on a card that cannot draw it, and a label has to
 * exist for the option. Those are the two places a type has to be known, and
 * neither is a menu.
 *
 * What makes it different from `line` is the *data*, not the drawing: the card
 * feeds it a series over time rather than two category rows. Which is exactly
 * why it cannot be offered generally -- on any other card it would be `line`
 * with a different name.
 */

/**
 * What suits a series measured over time.
 *
 * No donut: a donut divides a whole into parts, and twelve months of median
 * response time is not a whole that has parts. Stacked bars are offered because
 * for counts per bucket the total genuinely is meaningful.
 */
export const TIME_SERIES_CHARTS: ChartType[] = [
  "line",
  "area",
  "bar",
  "stacked-bar",
  "table",
];

/**
 * What suits a fixed set of categories — pipelines, on-time against late.
 *
 * No line or area: joining unordered categories with a line implies a
 * progression between them that does not exist, and the slope invites people to
 * read a trend out of alphabetical order.
 */
export const CATEGORY_CHARTS: ChartType[] = [
  "donut",
  "bar",
  "stacked-bar",
  "table",
];

/**
 * The chart to draw, given what was stored and what this card supports.
 *
 * A stored choice can outlive the card that stored it — a card's supported set
 * gets narrowed, or the layout came from somebody else's template — so a value
 * that is no longer offered falls back rather than rendering nothing.
 */
export function resolveChart(
  stored: string | undefined,
  supported: readonly ChartType[],
): ChartType {
  const known = supported.find((type) => type === stored);
  return known ?? supported[0]!;
}
